import { useEffect, useState } from 'react';
import { api } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Play, CalendarClock, Users, BellRing } from 'lucide-react';
import { ToggleRow, ChipInput } from './SettingsControls';
import { DigestSettings } from './DigestSettings';

const RECIPIENTS = [
  { key: 'notify_employee', label: 'Çalışanın kendisi', desc: 'Kendi belgesi için bildirim alır' },
  { key: 'notify_firm_admins', label: 'Firma adminleri', desc: 'Sadece kendi firmasındaki çalışanlar için' },
  { key: 'notify_full_admins', label: 'Full adminler', desc: 'Yetkili oldukları firmalardaki çalışanlar için' },
  { key: 'notify_super_admins', label: 'Süper adminler', desc: 'Tüm çalışanlar için' },
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR') : '—');

const StatusCard = ({ meta, cfg }) => (
  <Card data-testid="expiry-current-status">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Mevcut Durum</CardTitle>
    </CardHeader>
    <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><p className="text-xs text-slate-500">Durum</p><p className="font-medium" data-testid="expiry-status-enabled">{cfg.enabled ? 'Açık' : 'Kapalı'}</p></div>
      <div><p className="text-xs text-slate-500">Uyarı günleri</p><p className="font-medium" data-testid="expiry-status-thresholds">{cfg.thresholds.join(', ')} gün kala</p></div>
      <div><p className="text-xs text-slate-500">Son çalışma</p><p className="font-medium">{fmt(meta.last_run_at)}{meta.last_run_result ? ` · ${meta.last_run_result.notifications} bildirim` : ''}</p></div>
      <div><p className="text-xs text-slate-500">Sonraki çalışma</p><p className="font-medium" data-testid="expiry-status-next-run">{cfg.enabled ? fmt(meta.next_run_at) : '—'}</p></div>
      <p className="col-span-2 md:col-span-4 text-xs text-slate-500">
        Kontrol edilen belgeler: Kimlik Kartı, Ehliyet, Bestuurdersattest. İşten çıkmış çalışanlar hariç tutulur, aynı uyarı iki kez gönderilmez.
      </p>
    </CardContent>
  </Card>
);

export const ExpiryAlertSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    const r = await api.get('/admin/expiry-notifications/config');
    const { config, ...rest } = r.data.data;
    setCfg(config); setMeta(rest);
  };
  useEffect(() => { load().catch(() => toast.error('Ayarlar yüklenemedi')); }, []);

  if (!cfg) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/expiry-notifications/config', cfg); toast.success('Ayarlar kaydedildi'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try { const r = await api.post('/admin/expiry-notifications/run-now'); toast.success(r.data.message); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Çalıştırılamadı'); }
    finally { setRunning(false); }
  };
  const addThreshold = (v) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n) || n < 0 || n > 365) return toast.error('0-365 arası bir gün girin');
    set('thresholds', [...new Set([...cfg.thresholds, n])].sort((a, b) => b - a));
  };

  return (
    <div className="space-y-6" data-testid="expiry-alert-settings">
      <StatusCard meta={meta} cfg={cfg} />
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><BellRing className="h-4 w-4" /> Belge Süresi Uyarıları</CardTitle>
              <CardDescription>Otomatik günlük kontrol</CardDescription>
            </div>
            <Switch data-testid="expiry-enabled-switch" checked={cfg.enabled} onCheckedChange={(v) => set('enabled', v)} />
          </div>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Kaç gün kala uyarılsın?</Label>
            <ChipInput testId="expiry-threshold" type="number" placeholder="örn. 30" items={cfg.thresholds.map(String)}
              onAdd={addThreshold} onRemove={(v) => set('thresholds', cfg.thresholds.filter((t) => String(t) !== v))} />
          </div>
          <div className="space-y-2">
            <Label>Kontrol saati (UTC)</Label>
            <Input data-testid="expiry-hour-input" type="number" min={0} max={23} value={cfg.hour}
              onChange={(e) => set('hour', parseInt(e.target.value || '0', 10))} className="w-28" />
            <p className="text-xs text-slate-500">Belçika saati = UTC+1 (kış) / UTC+2 (yaz). Örn. 7 → yazın 09:00, kışın 08:00.</p>
          </div>
          <div className="space-y-1">
            <Label>Kanallar</Label>
            <ToggleRow id="channel-push" label="Uygulama içi bildirim" checked={cfg.channel_push} onChange={(v) => set('channel_push', v)} />
            <ToggleRow id="channel-email" label="E-posta" checked={cfg.channel_email} onChange={(v) => set('channel_email', v)} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Kimlere gönderilsin?</CardTitle>
          <CardDescription>Adminler, tüm çalışanların listesini tek bir özet bildirimde alır</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-6">
          <div>{RECIPIENTS.map((r) => <ToggleRow key={r.key} id={r.key} {...r} checked={cfg[r.key]} onChange={(v) => set(r.key, v)} />)}</div>
          <div className="space-y-2">
            <Label>Ek e-posta adresleri</Label>
            <p className="text-xs text-slate-500">Sisteme kayıtlı olmayan kişiler (örn. muhasebe) için yalnızca e-posta gider</p>
            <ChipInput testId="expiry-extra-email" type="email" placeholder="ornek@firma.be" items={cfg.extra_emails}
              onAdd={(v) => set('extra_emails', [...new Set([...cfg.extra_emails, v.toLowerCase()])])}
              onRemove={(v) => set('extra_emails', cfg.extra_emails.filter((e) => e !== v))} />
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end gap-2">
        <Button data-testid="expiry-run-now-btn" variant="outline" onClick={runNow} disabled={running}>
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Şimdi Çalıştır
        </Button>
        <Button data-testid="expiry-save-btn" onClick={save} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Kaydet
        </Button>
      </div>
      <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Admin Özetleri</h3>
        <DigestSettings expiryCfg={cfg} />
      </div>
    </div>
  );
};
